// Stable per-browser-session identifier and ad-frequency cap helpers.
// Cap is enforced client-side per (campaign × placement) per visitor session/window.

const SESSION_KEY = 'ad-session-id';
const SEEN_KEY = 'ad-seen-counts-v1';
const SESSION_WINDOW_HOURS = 24;
const DEFAULT_CAP = 3; // max impressions per campaign per session window per placement

export function getAdSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

type SeenMap = Record<string, { count: number; first: number }>;

function readSeen(): SeenMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return {};
    const parsed: SeenMap = JSON.parse(raw);
    const cutoff = Date.now() - SESSION_WINDOW_HOURS * 3600 * 1000;
    let mutated = false;
    for (const k of Object.keys(parsed)) {
      if (!parsed[k] || parsed[k].first < cutoff) {
        delete parsed[k];
        mutated = true;
      }
    }
    if (mutated) localStorage.setItem(SEEN_KEY, JSON.stringify(parsed));
    return parsed;
  } catch {
    return {};
  }
}

function writeSeen(map: SeenMap) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

const keyOf = (campaignId: string, placement: string) => `${campaignId}::${placement}`;

/** Returns true if the given campaign+placement has hit the cap and should be hidden. */
export function isCapped(campaignId: string, placement: string, cap = DEFAULT_CAP): boolean {
  const map = readSeen();
  const entry = map[keyOf(campaignId, placement)];
  if (!entry) return false;
  return entry.count >= cap;
}

/** Records that an impression was shown. Returns the new count. */
export function recordImpression(campaignId: string, placement: string): number {
  const map = readSeen();
  const k = keyOf(campaignId, placement);
  const existing = map[k];
  if (existing) {
    existing.count += 1;
  } else {
    map[k] = { count: 1, first: Date.now() };
  }
  writeSeen(map);
  return map[k].count;
}

export function getFrequencyCap() {
  return DEFAULT_CAP;
}

/** Returns the recorded impression count for a (campaign × placement) in the current window. */
export function getImpressionCount(campaignId: string, placement: string): number {
  const map = readSeen();
  return map[keyOf(campaignId, placement)]?.count ?? 0;
}

/** Test/debug helper — reset all frequency-cap state. */
export function resetAdFrequency() {
  try {
    localStorage.removeItem(SEEN_KEY);
  } catch {
    /* ignore */
  }
}
